import { Check, Clock3, X } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { formatTimeEntry, parseTimeEntry, shiftTime, timeSteps, type HourCycle } from '@/lib/date-entry';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { controlShell, shellButton, shellInput } from './control-styles';
import { assignRef, RejectionBubble, useRejectedFlash } from './entry-helpers';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

export interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  step?: number;
  hourCycle?: HourCycle;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  autoFocus?: boolean;
  isTimeDisabled?: (time: string) => boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

function nearestOption(options: string[], value: string): string {
  const target = value || parseTimeEntry('now') || '00:00';
  return [...options].reverse().find((option) => option <= target) ?? options[0];
}

function TimeList({
  options,
  value,
  hourCycle,
  isTimeDisabled,
  onPick,
}: {
  options: string[];
  value: string;
  hourCycle: HourCycle;
  isTimeDisabled?: (time: string) => boolean;
  onPick: (time: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const anchor = nearestOption(options, value);

  useEffect(() => {
    const current = listRef.current?.querySelector<HTMLButtonElement>(`[data-time="${anchor}"]`);
    current?.scrollIntoView({ block: 'center' });
    current?.focus({ preventScroll: true });
  }, [anchor]);

  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = [
      ...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? []),
    ];
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const jump: Record<string, number> = { ArrowDown: 1, ArrowUp: -1, PageDown: 8, PageUp: -8 };
    let next = -1;
    if (event.key in jump) next = Math.min(buttons.length - 1, Math.max(0, at + jump[event.key]));
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    if (next === -1) return;
    event.preventDefault();
    buttons[next]?.focus();
    buttons[next]?.scrollIntoView({ block: 'nearest' });
  };

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label="Times"
      onKeyDown={onKey}
      className="max-h-64 w-44 overflow-y-auto p-1"
    >
      {options.map((time) => {
        const selected = time === value;
        const blocked = !selected && Boolean(isTimeDisabled?.(time));
        return (
          <button
            key={time}
            type="button"
            role="option"
            aria-selected={selected}
            aria-disabled={blocked || undefined}
            disabled={blocked}
            data-time={time}
            tabIndex={time === anchor && !blocked ? 0 : -1}
            onClick={() => onPick(time)}
            className={cn(
              'flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm tabular-nums transition-colors focus-visible:outline-none',
              selected
                ? 'bg-primary/10 font-medium text-primary'
                : blocked
                  ? 'cursor-not-allowed text-muted-foreground/40 line-through'
                  : 'text-foreground hover:bg-accent focus-visible:bg-accent',
            )}
          >
            {formatTimeEntry(time, hourCycle)}
            {selected && <Check className="h-3.5 w-3.5" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}

export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(function TimePicker(
  {
    value,
    onChange,
    step = 15,
    hourCycle = 12,
    required,
    disabled,
    placeholder = hourCycle === 24 ? '09:30' : '9:30 am',
    id,
    name,
    className,
    autoFocus,
    isTimeDisabled,
    ...aria
  },
  ref,
) {
  const show = (time: string) => formatTimeEntry(time, hourCycle);
  const common = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => show(value));
  const [editing, setEditing] = useState(false);
  const { reason, flash, settle } = useRejectedFlash();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const interactedOutside = useRef(false);

  useEffect(() => {
    if (!editing) setText(formatTimeEntry(value, hourCycle));
  }, [value, editing, hourCycle]);

  const accept = (time: string) => {
    if (time && time !== value && isTimeDisabled?.(time)) {
      setText(show(value));
      flash('That time is not available');
      return;
    }
    settle();
    setText(show(time));
    if (time !== value) onChange(time);
  };

  const commit = (raw: string) => {
    const parsed = parseTimeEntry(raw);
    if (parsed === null || (parsed === '' && required)) {
      setText(show(value));
      flash(
        parsed === null ? `Could not read “${raw.trim()}” as a time` : 'A time is required here',
      );
      return;
    }
    accept(parsed);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if ((event.altKey && event.key === 'ArrowDown') || event.key === 'F4') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const base = parseTimeEntry(text) || value || nearestOption(timeSteps(step), '');
      accept(shiftTime(base, event.key === 'ArrowUp' ? step : -step));
    } else if (event.key === 'Enter' && text !== show(value)) {
      event.preventDefault();
      commit(text);
    } else if (event.key === 'Escape' && text !== show(value)) {
      event.preventDefault();
      setText(show(value));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={shellRef}
          data-disabled={disabled || undefined}
          className={cn(
            controlShell,
            'group/picker flex h-9 items-center',
            reason && 'border-destructive ring-[3px] ring-destructive/20',
            className,
          )}
        >
          <input
            ref={(node) => {
              inputRef.current = node;
              assignRef(ref, node);
            }}
            id={id}
            name={name}
            value={text}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            placeholder={placeholder}
            data-slot="time-input"
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            aria-label={aria['aria-label']}
            aria-labelledby={aria['aria-labelledby']}
            aria-describedby={aria['aria-describedby']}
            aria-invalid={aria['aria-invalid'] || Boolean(reason) || undefined}
            onFocus={() => setEditing(true)}
            onBlur={() => {
              setEditing(false);
              commit(text);
            }}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            className={cn(shellInput, 'tabular-nums')}
          />
          <RejectionBubble reason={reason} anchor={shellRef} onDismiss={settle} />
          {value && !required && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={common.t('time.clear')}
              onClick={() => {
                accept('');
                inputRef.current?.focus();
              }}
              className="hidden h-6 w-6 shrink-0 place-items-center rounded text-muted-foreground/70 transition-colors hover:text-foreground group-focus-within/picker:grid group-hover/picker:grid"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={common.t('time.choose')}
              className={shellButton}
            >
              <Clock3 className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          interactedOutside.current = false;
        }}
        onInteractOutside={() => {
          interactedOutside.current = true;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (!interactedOutside.current) inputRef.current?.focus();
        }}
      >
        <TimeList
          options={timeSteps(step)}
          value={value}
          hourCycle={hourCycle}
          isTimeDisabled={isTimeDisabled}
          onPick={(time) => {
            accept(time);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
});
